import React from "react";
import { CREST_SYMBOL_IMAGES } from "./crestSymbolImages";
import { CREST_FOLIAGE_IMAGES } from "./crestFoliageImages";
import { CREST_CROWN_IMAGES } from "./crestCrownImages";
import { crestFont, crestFontStack } from "./crestFonts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CX = 130;

function symbolSrc(url) {
  if (!url) return null;
  return url.startsWith("/api") ? `${BACKEND_URL}${url}` : url;
}

// Derive HSL hue & saturation from a hex color.
function hexToHS(hex) {
  let h = (hex || "").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length < 6) return { h: 42, s: 0.586 };
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  let hue = 0;
  if (d) {
    if (max === r) hue = 60 * (((g - b) / d) % 6);
    else if (max === g) hue = 60 * ((b - r) / d + 2);
    else hue = 60 * ((r - g) / d + 4);
  }
  if (hue < 0) hue += 360;
  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  return { h: hue, s };
}

// Build an SVG-filter recipe that shifts the crown's gold toward the crest accent colour.
const GOLD_H = 42, GOLD_S = 0.586;
function crownTint(accent) {
  const { h, s } = hexToHS(accent || "#D4AF5A");
  let delta = h - GOLD_H;
  while (delta > 180) delta -= 360;
  while (delta < -180) delta += 360;
  let satFactor = s / GOLD_S;
  satFactor = Math.max(0, Math.min(1.5, satFactor));
  const active = Math.abs(delta) > 1.5 || Math.abs(satFactor - 1) > 0.04;
  const id = "crownTint-" + (accent || "gold").replace(/[^a-z0-9]/gi, "");
  return { id, delta, satFactor, active };
}

// ---- Shield shapes (viewBox 0 0 260 340) ----
function ShieldPath({ shape, fill, stroke, inner, strokeWidth }) {
  const common = { fill, stroke, strokeWidth, strokeLinejoin: "round" };
  if (shape === "round") {
    return (
      <>
        <circle cx={CX} cy={168} r={72} {...common} />
        {inner && <circle cx={CX} cy={168} r={62} fill="none" stroke={inner} strokeWidth={2} />}
      </>
    );
  }
  if (shape === "diamond") {
    const d = `M${CX} 90 L214 168 L${CX} 250 L46 168 Z`;
    const di = `M${CX} 104 L200 168 L${CX} 236 L60 168 Z`;
    return (
      <>
        <path d={d} {...common} />
        {inner && <path d={di} fill="none" stroke={inner} strokeWidth={2} strokeLinejoin="round" />}
      </>
    );
  }
  if (shape === "heart") {
    const d = `M${CX} 252 C60 206 54 132 92 108 Q${CX} 92 ${CX} 132 Q${CX} 92 168 108 C206 132 200 206 ${CX} 252 Z`;
    const di = `M${CX} 240 C74 202 70 140 98 122 Q${CX} 110 ${CX} 140 Q${CX} 110 162 122 C190 140 186 202 ${CX} 240 Z`;
    return (
      <>
        <path d={d} {...common} />
        {inner && <path d={di} fill="none" stroke={inner} strokeWidth={2} strokeLinejoin="round" />}
      </>
    );
  }
  if (shape === "soft") {
    const d = `M74 96 Q64 88 64 108 V168 Q64 226 ${CX} 258 Q196 226 196 168 V108 Q196 88 186 96 Q${CX} 108 74 96 Z`;
    const di = `M80 108 Q72 102 72 116 V168 Q72 220 ${CX} 248 Q188 220 188 168 V116 Q188 102 180 108 Q${CX} 118 80 108 Z`;
    return (
      <>
        <path d={d} {...common} />
        {inner && <path d={di} fill="none" stroke={inner} strokeWidth={2} strokeLinejoin="round" />}
      </>
    );
  }
  // classic
  const d = `M64 96 H196 V168 Q196 228 ${CX} 260 Q64 228 64 168 Z`;
  const di = `M74 106 H186 V166 Q186 220 ${CX} 248 Q74 220 74 166 Z`;
  return (
    <>
      <path d={d} {...common} />
      {inner && <path d={di} fill="none" stroke={inner} strokeWidth={2} strokeLinejoin="round" />}
    </>
  );
}

// ---- Heraldic symbols (line art, centered ~ CX,168) ----
function Symbol({ symbol, color, customUrl, tint }) {
  const isCustom = symbol === "custom";
  const src = isCustom ? symbolSrc(customUrl) : (CREST_SYMBOL_IMAGES[symbol] || CREST_SYMBOL_IMAGES.tree);
  if (!src) return null;
  const size = 122;
  return (
    <image
      href={src}
      x={CX - size / 2}
      y={168 - size / 2}
      width={size}
      height={size}
      preserveAspectRatio="xMidYMid meet"
      filter={!isCustom && tint ? `url(#${tint})` : undefined}
    />
  );
}

// ---- Crown topper (selectable artwork) sits just above the shield, no overlap ----
function CrownImage({ style, tint }) {
  const src = CREST_CROWN_IMAGES[style] || CREST_CROWN_IMAGES.royal_classic;
  if (!src) return null;
  return (
    <image href={src} x={69} y={8} width={122} height={76}
      preserveAspectRatio="xMidYMax meet"
      filter={tint ? `url(#${tint})` : undefined} />
  );
}

// ---- Foliage wreath (laurel / olive / floral) — high-quality artwork framing the shield ----
function FoliageWreath({ style }) {
  const src = CREST_FOLIAGE_IMAGES[style];
  if (!src) return null;
  return (
    <image href={src} x={2} y={44} width={256} height={252}
      preserveAspectRatio="xMidYMid meet" />
  );
}

// ---- Banner with family name ----
// The name auto-fits every banner style: it starts at a natural size and, when the
// estimated width would overflow the banner, `textLength` compresses it so it never clips.
function Banner({ style, color, textColor, text, fontId, align, offsetY }) {
  const y = 250;
  let d;
  let maxW;
  let bandCenter; // vertical center of the coloured band, per style
  if (style === "straight") { d = `M34 ${y} H226 V${y + 34} H34 Z`; maxW = 178; bandCenter = y + 17; }
  else if (style === "folded") { d = `M26 ${y} L42 ${y + 8} H218 L234 ${y} L218 ${y + 40} H42 Z`; maxW = 158; bandCenter = y + 23; }
  else if (style === "ribbon") { d = `M44 258 Q${CX} 252 216 258 L236 252 L228 266 L236 280 L214 274 Q${CX} 268 46 274 L24 280 L32 266 L24 252 Z`; maxW = 146; bandCenter = 263; }
  else { d = `M24 ${y + 4} L44 ${y + 20} L24 ${y + 40} L56 ${y + 34} H204 L236 ${y + 40} L216 ${y + 20} L236 ${y + 4} L204 ${y + 12} H56 Z`; maxW = 150; bandCenter = y + 23; }

  const font = crestFont(fontId);
  const isScript = fontId === "script";
  const label = (text || "THE FAMILY").toUpperCase();
  let fontSize = label.length > 22 ? 11 : label.length > 17 ? 13 : 15.5;
  if (isScript) fontSize += 3; // script fonts read smaller at the same size
  const letterSpacing = isScript ? 0 : label.length > 17 ? 0.5 : 1.0;

  const est = label.length * fontSize * font.widthFactor + Math.max(0, label.length - 1) * letterSpacing;
  const fit = est > maxW ? { textLength: maxW, lengthAdjust: "spacingAndGlyphs" } : {};

  // Horizontal alignment inside the banner (default centered) + optional vertical nudge.
  const a = align || "center";
  const anchor = a === "left" ? "start" : a === "right" ? "end" : "middle";
  const pad = 6;
  const tx = a === "left" ? CX - maxW / 2 + pad : a === "right" ? CX + maxW / 2 - pad : CX;
  // Baseline that visually centres the caps in the band, plus the user's vertical nudge.
  const baselineY = bandCenter + fontSize * 0.35 + (Number(offsetY) || 0);

  return (
    <g>
      <path d={d} fill={color} stroke={color} strokeWidth={1.5} strokeLinejoin="round" />
      <text x={tx} y={baselineY} textAnchor={anchor} fontFamily={font.stack}
        fontSize={fontSize} fontWeight="700" letterSpacing={letterSpacing} fill={textColor} {...fit}>
        {label}
      </text>
    </g>
  );
}

export function CrestSVG({ crest, size = 260, title }) {
  if (!crest || crest.crestPlacement === "hidden_render") return null;
  const shape = crest.shape || crest.shieldShape || "classic";
  const year = crest.establishedYear || crest.year || "";
  const accent = crest.accentColor || "#D4AF5A";
  const primary = crest.primaryColor || "#071B33";
  const inner = crest.doubleShieldBorder ? (crest.secondaryColor || "#F8F0DE") : null;
  const fontStack = crestFontStack(crest.fontFamily);
  const tint = crownTint(accent);

  return (
    <svg viewBox="0 0 260 340" width={size} height={size * (340 / 260)} role="img"
      aria-label={title || `${crest.familyName || "Family"} crest`} style={{ display: "block", overflow: "visible" }}>
      <defs>
        <filter id="crestShadow" x="-20%" y="-20%" width="140%" height="150%">
          <feDropShadow dx="0" dy="6" stdDeviation="6" floodOpacity="0.25" />
        </filter>
        {tint.active && (
          <filter id={tint.id} colorInterpolationFilters="sRGB">
            <feColorMatrix type="saturate" values={String(tint.satFactor)} />
            <feColorMatrix type="hueRotate" values={String(tint.delta)} />
          </filter>
        )}
      </defs>

      {crest.showFoliage && crest.foliageStyle !== "none" && (
        <FoliageWreath style={crest.foliageStyle} />
      )}

      <g filter="url(#crestShadow)">
        <ShieldPath shape={shape} fill={primary} stroke={accent} inner={inner} strokeWidth={7} />
      </g>

      <Symbol symbol={crest.symbol} color={accent} customUrl={crest.customSymbolUrl} bg={primary} tint={tint.active ? tint.id : null} />

      {crest.showCrown && <CrownImage style={crest.crownStyle} tint={tint.active ? tint.id : null} />}

      {crest.showBanner && crest.showFamilyName && (
        <Banner style={crest.bannerStyle} color={primary} textColor={crest.bannerTextColor || accent}
          text={crest.familyName} fontId={crest.fontFamily} align={crest.bannerTextAlign} offsetY={crest.bannerTextOffsetY} />
      )}

      {crest.showMotto && crest.motto && (
        <text x={CX} y={312} textAnchor="middle" fontFamily={fontStack} fontSize="13"
          fontWeight="600" letterSpacing="1.5" fill={crest.mottoTextColor || primary}>
          {crest.motto.toUpperCase()}
        </text>
      )}

      {crest.showEstablishedYear && year && (
        <text x={CX} y={332} textAnchor="middle" fontFamily={fontStack} fontSize="11"
          letterSpacing="2" fill={accent}>— EST. {year} —</text>
      )}
    </svg>
  );
}

export default CrestSVG;

// ---- Small option thumbnails for the editor buttons ----
export function ShapeThumb({ shape, color = "#334155" }) {
  return (
    <svg viewBox="0 0 260 340" width="34" height="40" aria-hidden="true">
      <g transform="translate(0,-30)">
        <ShieldPath shape={shape} fill={color} stroke={color} inner={null} strokeWidth={0} />
      </g>
    </svg>
  );
}

export function SymbolThumb({ symbol, color = "#334155", customUrl }) {
  return (
    <svg viewBox="60 100 140 140" width="30" height="30" aria-hidden="true">
      <Symbol symbol={symbol} color={color} customUrl={customUrl} />
    </svg>
  );
}

export function BannerThumb({ style, color = "#334155" }) {
  return (
    <svg viewBox="20 244 220 56" width="52" height="16" aria-hidden="true">
      <Banner style={style} color={color} textColor={color} text="" />
    </svg>
  );
}
